'use client';

import { IconEdit } from '@tabler/icons-react';
import {
  FetchInsurerClaimDetailResponseBody,
  UpdateClaimRequestBody,
} from 'bff';
import { useState, useCallback } from 'react';
import { Path, get } from 'react-hook-form';
import { useTranslations } from 'i11n';
import { useToast, Button } from 'ui';
import {
  ClaimUpdateSegment,
  useSafeClaimUpdate,
} from '../contexts/ClaimUpdateContext';
import { withEnablement } from '../hocs/withEnablement';
import { useApiError } from '../hooks/useApiError';

const collectErrorMessages = (error: unknown): string[] => {
  if (!error || typeof error !== 'object') return [];

  const { message, ref, type, ...nested } = error as Record<string, unknown>;

  if (typeof message === 'string' && message) return [message];

  return Object.values(nested).flatMap(collectErrorMessages);
};

const UnsafeEditClaimButton = ({
  segment,
  data,
  itemIndex,
}: {
  segment: ClaimUpdateSegment;
  data: FetchInsurerClaimDetailResponseBody;
  // When the segment is an array (e.g. claimants), only the row at this index
  // is edited: validation and the saved payload are scoped to it so sibling
  // rows the UI does not render cannot block the save nor get clobbered.
  itemIndex?: number;
}) => {
  const update = useSafeClaimUpdate();

  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();

  const t = useTranslations();

  const { handleError } = useApiError();

  // RHF accepts `claimants.2` as a valid path, but the template literal built
  // from the segment union is not narrowable to it, hence the assertion.
  const editPath = (
    itemIndex === undefined ? segment : `${segment}.${itemIndex}`
  ) as Path<UpdateClaimRequestBody>;

  const buildPayload = useCallback((): UpdateClaimRequestBody => {
    if (itemIndex === undefined)
      return { [segment]: update.form!.getValues(segment) };

    return { [segment]: [update.form!.getValues(editPath)] };
  }, [segment, itemIndex, editPath, update.form]);

  const handleSave = useCallback(
    (data: UpdateClaimRequestBody) => {
      setIsLoading(true);
      update.onSave!(data)
        .then(() => {
          if (update.onInvalidate) {
            update.onInvalidate();
          }

          toast({
            title: 'Reclamo actualizado',
            description:
              'Los cambios del reclamo fueron guardados exitosamente',
          });

          update.setEditingSegment!(undefined);
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    },
    [handleError, toast, update],
  );

  const notifyInvalidSegment = useCallback(() => {
    const messages = Array.from(
      new Set(
        collectErrorMessages(get(update.form!.formState.errors, editPath)),
      ),
    );

    toast({
      title: 'No se pudieron guardar los cambios',
      description: messages.length
        ? messages.join('. ')
        : 'Revisá los datos cargados del reclamo',
    });
  }, [editPath, toast, update.form]);

  if (update.editingSegment === segment)
    return (
      <div className='flex flex-row items-center space-x-2'>
        <Button
          onClick={() => {
            update.form!.reset();
            update.setEditingSegment!(undefined);
          }}
          disabled={isLoading}
          variant={'outline'}
          size='sm'
        >
          Cancelar
        </Button>
        <Button
          loading={isLoading}
          onClick={() => {
            if (isLoading) return;
            setIsLoading(true);

            update
              .form!.trigger(editPath)
              .then((isValid) => {
                if (isValid) {
                  handleSave(buildPayload());
                  return;
                }

                notifyInvalidSegment();
                setIsLoading(false);
              })
              .catch(() => setIsLoading(false));
          }}
          size='sm'
        >
          Guardar
        </Button>
      </div>
    );

  return (
    <Button
      size='sm'
      variant='ghost'
      onClick={() => {
        update.form!.reset(update.data);
        update.setEditingSegment!(segment);
      }}
    >
      <IconEdit className='w-4 h-4' />
    </Button>
  );
};

export const EditClaimButton = withEnablement(UnsafeEditClaimButton);
